import { Color, Spacing } from '@persona-id/design-tokens';
import { MouseEvent, useCallback, useEffect, useMemo } from 'react';
import { Control, Controller, useFormState } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt } from 'react-router';
import { useNavigate } from 'react-router-dom-v5-compat';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import { NAVBAR_EXPANDED_WIDTH } from 'dashboard/components/Navbar';
import Banner from 'dashboard/components-lib/Banner';
import FileUpload from 'dashboard/components-lib/FileUpload';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Backdrop } from 'dashboard/layouts/IndexViewLayoutV2';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import {
  ReportTemplateCustomList,
  ReportTemplateCustomListConfigFormData,
} from 'dashboard/models/report-template';
import {
  CustomListConfigHeaderMapping,
  CustomListFileType,
  ReportTemplateVersion,
  ReportTemplateVersionCustomList,
} from 'dashboard/models/report-template-version';
import { MatchGroup } from 'dashboard/models/verification-check';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { CustomListRunButton } from 'dashboard/views/DashboardReportTemplateVersionsShowView/CustomListRunButton';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { paragraphMixins } from 'design-system/mixins/text';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';

import { CustomListConfigHeaderMapper } from './CustomListConfigHeaderMapper';
import CustomListConfigMatchGroupsEditor from './CustomListConfigMatchGroupsEditor';
import { ReportTemplateVersionTimeline } from './ReportTemplateVersionTimeline';

export interface CustomListConfigEditorProps {
  version: ReportTemplateVersionCustomList;
  latestPublishedVersion: ReportTemplateVersion;
  reportTemplate: ReportTemplateCustomList;
  matchRequirementProfiles: MatchRequirementProfile[];
  canEdit: boolean;
  saveReportTemplateError?: Error | ApiNetworkError | ApiResponseError;
  isHeaderMappingsValidated: boolean;
  validateHeaderMappingsError?: Error | ApiNetworkError | ApiResponseError;
  onVersionChange: (versionId: string) => void;
  submit: (data: ReportTemplateCustomListConfigFormData) => Promise<void>;
  handleValidateHeaderMappings: (
    headerMappings: Array<CustomListConfigHeaderMapping>,
    customListFile: FileWithPreview,
  ) => Promise<void>;
  setIsHeaderMappingsValidated: (value: boolean) => void;
  handleUpdateMatchRequirementProfiles: (
    headerMappings: Array<CustomListConfigHeaderMapping>,
  ) => Promise<void>;
}

export const CustomListConfigEditor = ({
  version,
  latestPublishedVersion,
  reportTemplate,
  matchRequirementProfiles,
  canEdit,
  saveReportTemplateError,
  isHeaderMappingsValidated,
  validateHeaderMappingsError,
  onVersionChange,
  submit,
  handleValidateHeaderMappings,
  setIsHeaderMappingsValidated,
  handleUpdateMatchRequirementProfiles,
}: CustomListConfigEditorProps) => {
  const dispatch = useDispatch();

  const { handleSubmit, control, watch, register, reset, setValue } =
    useFormSecurely<ReportTemplateCustomListConfigFormData>({
      mode: 'onChange',
      defaultValues: {
        'header-mappings': version?.attributes['header-mappings'] || [],
        'match-groups': version?.attributes['match-groups'] || [],
      },
    });
  const [isPublishing, { enable: showPublishingModal, disable: hidePublishingModal }] =
    useToggle(false);
  const navigate = useNavigate();
  const { isSubmitting, isDirty, errors, isValid, isSubmitted } = useFormState({
    control,
  });
  const [isHistoryOpen, { toggle: toggleHistory, disable: closeHistory }] = useToggle(false);
  const isManualRunPolicy = version.attributes['run-policy'] === 'manual';

  const fileWatch = watch('custom-list-file');

  useEffect(() => {
    if (version?.attributes['header-mappings'].length > 0) {
      handleUpdateMatchRequirementProfiles(version.attributes['header-mappings']);
    }
  }, [version?.attributes['header-mappings']]);

  useEffect(() => {
    if (fileWatch) {
      // When the uploaded file is changed, if the new file contains all of the mapped headers
      // from the previously saved config, we preserve the existing header-mappings for convenience.
      // Otherwise, we reset them. The CustomListConfigMatchGroupsEditor component handles doing the same
      // for the match-groups.
      const allHeadersIncluded =
        version &&
        version.attributes['header-mappings'].every((mapping) =>
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          fileWatch.headers.includes(mapping['header-name']),
        );
      if (allHeadersIncluded) {
        setValue('header-mappings', version?.attributes['header-mappings']);
        setIsHeaderMappingsValidated(true);
      } else {
        setValue('header-mappings', []);
        setValue('match-groups', []);
        setIsHeaderMappingsValidated(false);
      }
    }
  }, [fileWatch]);

  useEffect(() => {
    if (isSubmitted) {
      reset({
        'custom-list-file': undefined,
        'change-description': undefined,
      });
    }
  }, [isSubmitted, reset]);

  // Note: CustomListConfigMatchGroupsEditor is a Redux Form due to MatchRequirementsView
  // and MatchRequirementPanel being used in InquiryTemplateEditor. Hence, we use this function
  // to sync matchGroups with the form data in CustomListConfigEditor, which uses RHF.
  // TODO: Fix the fact that this sets 'match-groups' as dirty when the user did not make any changes
  const matchGroups = watch('match-groups');
  const handleMatchGroupsUpdate = useCallback(
    (newMatchGroups: MatchGroup[]) => {
      setValue('match-groups', newMatchGroups, { shouldDirty: true });
    },
    [setValue],
  );

  // Because we use EMPTY_MATCH_GROUPS and EMPTY_MATCH_REQUIREMENT as placeholders, we need to check
  // more thoroughly if the match groups are actually empty
  const matchGroupsEmpty = () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (matchGroups.length === 0) return true;

    let noMatchRequirements = true;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    matchGroups.forEach((matchGroup) => {
      matchGroup['match-requirements'].forEach((matchRequirement) => {
        if (matchRequirement.name) {
          noMatchRequirements = false;
        }
      });
    });

    return noMatchRequirements;
  };

  // We filter out all of the empty placeholder match requirements before they are saved, since the
  // matchGroupsEmpty validation does not catch this if there is at least one match group that is filled out
  const filterMatchGroups = () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const filteredMatchGroups = matchGroups.map((matchGroup) => ({
      ...matchGroup,
      'match-requirements': matchGroup['match-requirements'].filter(
        (matchRequirement) => matchRequirement.name,
      ),
    }));
    const filteredAndNonEmptyMatchGroups = filteredMatchGroups.filter(
      (matchGroup) => matchGroup['match-requirements'].length > 0,
    );

    setValue('match-groups', filteredAndNonEmptyMatchGroups);
  };

  useApiErrorHandler({
    error: saveReportTemplateError,
    onError: () => {
      hidePublishingModal();
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onNetworkError: () => {},
  });

  if (!version) {
    return <LoadingIndicator fullscreen />;
  }

  const isRunning =
    version?.attributes['custom-list-file-name'] && version?.attributes['run-status'] === 'running';
  const permanentlyDisabled = !canEdit || isRunning || latestPublishedVersion?.id !== version?.id;

  // TODO(rpeng): extract modal into separate component and bubble up form
  // top level form can validate everything except change-description, and modal form validates change-description
  return (
    <form>
      <ModalWrapper isOpen={isPublishing} handleCloseModal={hidePublishingModal}>
        <ModalContainer>
          <ModalHeader onClose={hidePublishingModal}>Publish new version</ModalHeader>
          <ModalDescription>
            {isManualRunPolicy
              ? 'This will create a new template version with the uploaded custom list. ' +
                'To start screening, click on Run after saving changes.'
              : 'This will create a new template version and run for all Reports.'}
          </ModalDescription>
          <PublishModalBody>
            <FormControlLabel>Description</FormControlLabel>
            <TextArea
              {...register('change-description', {
                required: 'Please enter a change description.',
                maxLength: {
                  value: 255,
                  message: 'Please limit description to 255 characters',
                },
              })}
              placeholder='Change description'
              disabled={isSubmitting}
            />
            {errors['change-description'] && (
              <FormError>{errors['change-description'].message}</FormError>
            )}
          </PublishModalBody>
          <PublishModalFooter>
            <PublishModalButtonGroup>
              <Button
                id='custom-list-report-template-edit__cancel-button'
                variant='basic'
                disabled={isSubmitting && !isValid}
                width='auto'
                onClick={hidePublishingModal}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                id='custom-list-report-template-edit__submit-button'
                type='submit'
                width='auto'
                onClick={() => {
                  filterMatchGroups();
                  handleSubmit(async (data: ReportTemplateCustomListConfigFormData) => {
                    await submit(data);
                    dispatch(
                      showMessage(
                        'A new version has been saved. You will receive an email when all reports have finished processing.',
                        MessageType.Success,
                      ),
                    );
                    hidePublishingModal();
                  })();
                }}
                disabled={isSubmitting || !isValid}
                isPending={isSubmitting}
              >
                {isManualRunPolicy ? 'Save changes' : 'Run reports'}
              </Button>
            </PublishModalButtonGroup>
          </PublishModalFooter>
        </ModalContainer>
      </ModalWrapper>
      {/** Note this logic will need to be updated when we support other run policies */}
      {isRunning && (
        <Banner backgroundColor={Color.CoreYellow400}>
          Report runs currently in progress. No edits can be made.
        </Banner>
      )}
      {latestPublishedVersion?.id !== version?.id && (
        <Banner backgroundColor={Color.CoreYellow400}>
          You are viewing a previous version that can no longer be edited.
        </Banner>
      )}
      <Prompt
        when={isDirty && !isSubmitting}
        message='You have unsaved changes. Are you sure you want to leave?'
      />
      <HeaderContainer>
        <HeaderTitle>
          <NavBack to={`/dashboard/report-templates/${reportTemplate?.id}`} />
          <span>{!reportTemplate ? '' : `${reportTemplate?.attributes.name} / Configuration`}</span>
        </HeaderTitle>
        <HStack>
          {isManualRunPolicy && (
            <CustomListRunButton
              width='auto'
              variant='primary'
              reportTemplateVersion={version}
              disabled={isSubmitting || permanentlyDisabled || isDirty}
            />
          )}
          <Button
            id='custom-list-report-template-edit__publish'
            onClick={() => showPublishingModal()}
            width='auto'
            variant='primary'
            disabled={
              permanentlyDisabled ||
              isSubmitting ||
              !isDirty ||
              (version?.attributes['custom-list-file-type'] ===
                CustomListFileType.CustomScreening &&
                fileWatch &&
                (!isHeaderMappingsValidated || matchGroupsEmpty())) ||
              (version?.attributes['custom-list-file-type'] ===
                CustomListFileType.CustomScreening &&
                !fileWatch &&
                matchGroupsEmpty())
            }
            isPending={isSubmitting}
          >
            Publish
          </Button>
          <ContextMenu
            side='bottom'
            items={[
              {
                id: 'run-history',
                onClick: () =>
                  navigate(
                    `/dashboard/report-templates/${reportTemplate?.id}/${version.id}/run-history`,
                  ),
                label: 'Run history',
              },
              {
                id: 'version-history',
                onClick: toggleHistory,
                label: 'Version history',
              },
            ]}
            renderItem={(item) => (
              <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                {item.label}
              </ContextMenu.Item>
            )}
          />
        </HStack>
      </HeaderContainer>
      <PageContainer>
        {isHistoryOpen && (
          <ReportTemplateVersionTimeline
            versions={reportTemplate.attributes['version-history']}
            currentVersionId={version.id}
            onClose={() => closeHistory()}
            onVersionSelect={onVersionChange}
          />
        )}
        {isSubmitting && <CardLoadingIndicator />}
        {version?.attributes['custom-list-file-type'] === CustomListFileType.CustomScreening ? (
          <>
            <ConfigStep
              title='1. Upload a CSV file & map your headers to specific fields'
              completed={
                (fileWatch && isHeaderMappingsValidated) ||
                (!fileWatch && version?.attributes['header-mappings'].length !== 0)
              }
              disabled={permanentlyDisabled || isSubmitting}
            >
              <>
                <FileUploader
                  permanentlyDisabled={permanentlyDisabled}
                  isSubmitting={isSubmitting}
                  version={version}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  fileWatch={fileWatch}
                  control={control}
                  reset={reset}
                />
                <Spacer height={2} />
                {fileWatch ? (
                  <Controller
                    name='header-mappings'
                    control={control}
                    render={({ field }) => (
                      <CustomListConfigHeaderMapper
                        customListFile={fileWatch}
                        mappableFields={version?.attributes['mappable-fields']}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        headerMappings={field.value}
                        isHeaderMappingsValidated={isHeaderMappingsValidated}
                        onHeaderMappingsChange={field.onChange}
                        validateHeaderMappingsError={validateHeaderMappingsError}
                        handleValidateHeaderMappings={handleValidateHeaderMappings}
                        setIsHeaderMappingsValidated={setIsHeaderMappingsValidated}
                      />
                    )}
                  />
                ) : (
                  <>
                    <Spacer height={2} />
                    <Paragraph color='primary'>
                      These are the header mappings you have configured. Upload a new file in order
                      to change these mappings.
                    </Paragraph>
                    <Spacer height={2} />
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeaderCell>CSV header name</TableHeaderCell>
                          <TableHeaderCell>Mapped field</TableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {version?.attributes['header-mappings'].map((mapping, i) => (
                          <TableRow
                            key={`custom-list-report-template-edit__header-mapping-row-${i}`}
                          >
                            <TableCell>{mapping['header-name']}</TableCell>
                            <TableCell>{mapping['mapped-field']}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </>
            </ConfigStep>
            <ConfigStep
              title='2. Configure your match logic'
              completed={
                (fileWatch && !matchGroupsEmpty()) ||
                (!fileWatch && version?.attributes['match-groups'].length !== 0)
              }
              disabled={
                permanentlyDisabled ||
                isSubmitting ||
                (fileWatch && !isHeaderMappingsValidated) ||
                (!fileWatch && version?.attributes['header-mappings'].length === 0)
              }
            >
              <Paragraph color='primary'>
                These are the rules that will be used to determine whether each Report is matched.
                Only the mapped fields selected above can be used in your rules.
              </Paragraph>
              <CustomListConfigMatchGroupsEditor
                version={version}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                fileWatch={fileWatch}
                matchRequirementProfiles={matchRequirementProfiles}
                handleMatchGroupsUpdate={handleMatchGroupsUpdate}
              />
            </ConfigStep>
          </>
        ) : (
          <ConfigStep
            title='Upload & preview your CSV file'
            completed={true}
            disabled={permanentlyDisabled || isSubmitting}
          >
            <FileUploader
              permanentlyDisabled={permanentlyDisabled}
              isSubmitting={isSubmitting}
              version={version}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              fileWatch={fileWatch}
              control={control}
              reset={reset}
            />
            <Spacer height={2} />
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <FincenDataPreview version={version} fileWatch={fileWatch} />
          </ConfigStep>
        )}
      </PageContainer>
    </form>
  );
};

interface ConfigStepProps {
  title: string;
  completed: boolean;
  disabled: boolean;
  children: React.ReactNode;
}

const ConfigStep = ({ title, completed = false, disabled = false, children }: ConfigStepProps) => {
  return (
    <ConfigStepWrapper>
      <ConfigStepHeader>
        {title}
        {completed ? (
          <Sticker variant='success'>Completed</Sticker>
        ) : (
          <Sticker variant='caution'>Not completed</Sticker>
        )}
      </ConfigStepHeader>
      <ConfigStepBody>
        {disabled ? (
          <Paragraph>This step cannot be edited at this time.</Paragraph>
        ) : (
          <>{children}</>
        )}
      </ConfigStepBody>
    </ConfigStepWrapper>
  );
};

interface FileUploaderProps {
  permanentlyDisabled: boolean;
  isSubmitting: boolean;
  version: ReportTemplateVersionCustomList;
  fileWatch: FileWithPreview | null;
  control: Control<ReportTemplateCustomListConfigFormData>;
  reset: (values?: ReportTemplateCustomListConfigFormData) => void;
}

const FileUploader = ({
  permanentlyDisabled,
  isSubmitting,
  version,
  fileWatch,
  control,
  reset,
}: FileUploaderProps) => {
  const MAX_CSV_FILE_SIZE_ROWS = 10000;
  const MAX_CSV_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB in bytes

  const filePreview = (() => {
    if (fileWatch) {
      return fileWatch.name;
    } else if (version?.attributes['custom-list-file-name']) {
      return version?.attributes['custom-list-file-name'];
    } else {
      return 'No CSV file has been uploaded yet!';
    }
  })();

  return (
    <fieldset disabled={permanentlyDisabled || isSubmitting}>
      <Controller
        control={control}
        name='custom-list-file'
        render={({ field: { onChange, value } }) => {
          return (
            <StyledFileUpload
              accept={['text/csv']}
              disabled={permanentlyDisabled || isSubmitting}
              label={
                <>
                  <Paragraph>{filePreview}</Paragraph>
                  {!permanentlyDisabled && (
                    <Button
                      id='report-template-custom-list__upload-csv'
                      width='auto'
                      variant='primary'
                      disabled={permanentlyDisabled || isSubmitting}
                    >
                      {!fileWatch && !version?.attributes['custom-list-file-name']
                        ? 'Upload CSV'
                        : 'Replace CSV'}
                    </Button>
                  )}
                </>
              }
              clearButton={(
                onClear: (e: MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => void,
              ) => {
                const hasPublishedFile = !!version?.attributes['custom-list-file-name'];
                return (
                  <Button
                    id='report-template-custom-list__file-upload-clear'
                    width='auto'
                    variant='primary'
                    onClick={onClear}
                  >
                    {hasPublishedFile ? 'Undo' : 'Clear File'}
                  </Button>
                );
              }}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              preview={null}
              value={value}
              onChange={(data) => {
                if (
                  data &&
                  (data.size > MAX_CSV_FILE_SIZE_BYTES ||
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    data.dataPreview.length > MAX_CSV_FILE_SIZE_ROWS)
                ) {
                  alert(
                    'The file you are trying to upload is too large. Please upload a file that has up to 10,000 rows and is less than 5MB.',
                  );
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                } else if (data && new Set(data.headers).size !== data.headers.length) {
                  alert(
                    'The file you are trying to upload has multiple headers with the same name. Please make sure all headers are unique.',
                  );
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                } else if (data && data.headers.length === 0) {
                  alert(
                    'The file you are trying to upload has no headers. Please upload a valid CSV.',
                  );
                } else {
                  onChange(data);
                }

                // NOTE: this solves a quirk with RHF where it still thinks the value is dirty even if we set it to
                // null. Need to refactor this once we start adding more configs to this form
                if (!data) {
                  reset();
                }
              }}
            />
          );
        }}
      />
    </fieldset>
  );
};

interface FincenDataPreviewProps {
  version: ReportTemplateVersionCustomList;
  fileWatch: FileWithPreview | null;
}

const FincenDataPreview = ({ version, fileWatch }: FincenDataPreviewProps) => {
  const FINCEN_PERSON_HEADERS = [
    'tracking_number',
    'last_name',
    'first_name',
    'middle_name',
    'suffix',
    'alias_last_name',
    'alias_first_name',
    'alias_middle_name',
    'alias_suffix',
    'number',
    'number_type',
    'dob',
    'street',
    'city',
    'state',
    'zip',
    'country',
    'phone',
  ];

  const FINCEN_BUSINESS_HEADERS = [
    'tracking_number',
    'business_name',
    'dba_name',
    'number',
    'number_type',
    'incorporated',
    'street',
    'city',
    'state',
    'zip',
    'country',
    'phone',
  ];

  const headers =
    version?.attributes['custom-list-file-type'] === CustomListFileType.FincenBusiness
      ? FINCEN_BUSINESS_HEADERS
      : FINCEN_PERSON_HEADERS;

  const rows = useMemo(() => {
    const list = version?.attributes['custom-list'];
    if (!list || fileWatch != null) {
      return [];
    }
    const newList = [...list];
    newList.shift();
    return newList;
  }, [version?.attributes, fileWatch]);

  const isImpersonated = useSelector<RootState, boolean>(
    (state) => !!state.currentUser.impersonationMode,
  );

  return (
    <TableWrapper>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell key='row'></TableHeaderCell>
            {headers.map((header) => (
              <TableHeaderCell key={header}>{header}</TableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={`csv-row-${i}`}>
              <TableCell key={`csv-cell-${i}-row`}>
                <Paragraph color='disabled'>{i + 1}</Paragraph>
              </TableCell>
              {row.map((item, j) => (
                <TableCell key={`csv-cell-${i}-${j}`}>{item}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length <= 0 && (
        <>
          <Spacer height={5} />
          <EmptyState>
            <Paragraph>
              {isImpersonated
                ? 'Impersonated users cannot view custom list files.'
                : fileWatch == null
                  ? 'No file published'
                  : 'New file selected. Publish to see your changes.'}
            </Paragraph>
          </EmptyState>
          <Spacer height={5} />
        </>
      )}
    </TableWrapper>
  );
};

const HeaderContainer = styled(FullScreenHeaderContainer)`
  position: fixed;
  width: calc(100vw - ${NAVBAR_EXPANDED_WIDTH}px);
`;

const CardLoadingIndicator = styled(LoadingIndicator)`
  position: absolute;
  background-color: rgba(255, 255, 255, 0.7);
  height: 100%;
  width: 100%;
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
`;

const PageContainer = styled(Backdrop)`
  padding-top: 80px;
`;

const ConfigStepWrapper = styled(Card)`
  margin: ${Spacing[4]};
  border-radius: ${Spacing[2]};
`;

const ConfigStepHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${Spacing[2]};
  padding: ${Spacing[4]} ${Spacing[4]} ${Spacing[1]} ${Spacing[4]};
  font-size: 16px;
  font-weight: 500;
`;

const ConfigStepBody = styled.div`
  padding: ${Spacing[4]};
`;

const StyledFileUpload = styled(FileUpload)`
  height: auto;

  & > div {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  & > aside {
    justify-content: space-between;
    flex-direction: row-reverse;
    ${paragraphMixins.md}
  }
`;

const TableWrapper = styled.div`
  overflow: auto;
  margin: ${Spacing[4]} 0px;
`;

const EmptyState = styled.div`
  text-align: center;
`;

const PublishModalFooter = styled.footer`
  flex: none;
  padding: ${Spacing[7]};
  padding-top: 0;
  padding-bottom: ${Spacing[5]};
`;

const PublishModalButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: ${Spacing[2]};
`;

const PublishModalBody = styled(ModalBody)`
  padding-bottom: ${Spacing[5]};
`;
